/**
 * Втрата доступу до відкритого запису потоком SSE (AG-69, пп. а, б; #1065). Контракт і межі —
 * cross-cutting.md, розд. 11.3, «Втрата доступу до відкритого запису»; ціль і замір —
 * performance-budget-capabilities.md, розд. 6. Окремий модуль без zod: веб бере його підшляхом,
 * корінь контрактів тягне zod у чанки.
 *
 * - На підключенні сервер першою подією надсилає `stream` з id з'єднання й id людини (без `id:`,
 *   `Last-Event-ID` вона не рухає), і для нового з'єднання, і для добору.
 * - Вкладка повідомляє, які записи в неї відкриті: `PUT /events/watch` з цим id і всім переліком.
 *   Сервер лишає лише записи, видимі людині зараз, тож перелік нічого не розкриває.
 * - Перелік з'єднання сервер перевіряє, коли в пакеті подій є такий запис, на сигнал доступу людини
 *   (NOTIFY тригерів доступу) і на повторній перевірці потоку. Запис вийшов з видимості —
 *   `record.unavailable` з `{ entity, id }` (без `id:` і без жодного поля), і запис з переліку
 *   знімається. Не через outbox і не через CDC (умови Security). Подія службова, не доменна:
 *   у каталозі подій, автоматизаціях і CDC її немає (`record-events.ts`).
 *
 * Інваріанти, на які спирається злиття дублів (#1219, рев'ю Архітектора #1227, S6):
 * 1. `record.unavailable` пишеться після доменних подій того самого пакета (`sse.test.ts`):
 *    `record.deleted { reason: 'merge' }` доходить раніше.
 * 2. Запис, що зникає (зокрема поглинений), має бути `recordId` якоїсь події пакета; інакше подію
 *    дасть лише такт перевірки (≤ 30 с).
 * 3. Подія не несе причини: картка на неї лише перевіряє запис (`record-gone.ts`). Коли `GET`
 *    поглиненого запису віддаватиме `mergedInto`, перевірка має вести на «Запис об'єднано», а не на
 *    «Доступ втрачено».
 */

/** Подія SSE з id з'єднання. */
export const STREAM_EVENT = 'stream';
/** Подія SSE втрати доступу до запису з переліку з'єднання. */
export const RECORD_UNAVAILABLE_EVENT = 'record.unavailable';
/** Шлях переліку відносно шляху потоку (`/events` → `/events/watch`). */
export const STREAM_WATCH_SUFFIX = '/watch';
/** Записів у переліку з'єднання, не більше: стільки карток вкладка не тримає відкритими. */
export const MAX_WATCHED_RECORDS = 20;

/** Відкритий запис: дані `stream` і `record.unavailable` та елемент переліку. */
export interface WatchedRecord {
  entity: string;
  id: string;
}

/** Тіло `PUT /events/watch`: увесь перелік відкритих записів вкладки, не приріст. */
export interface StreamWatchBody {
  stream: string;
  records: WatchedRecord[];
}

/** Дані події `stream`. */
export interface StreamHello {
  stream: string;
  /**
   * Чий потік (`principalKey`, id людини): вкладка-лідер звіряє його з людиною вкладки й не
   * пересилає іншим вкладкам потік того, хто ввійшов у браузері після неї (UX-24).
   */
  user: string;
}

/**
 * Ключ запису в переліку з'єднання, у відповіді `visible` і в переліку вкладок браузера: один формат
 * скрізь, інакше розбіжність дасть хибний `record.unavailable` (рев'ю Архітектора #1227, N1).
 */
export const refKey = (record: WatchedRecord): string =>
  `${record.entity}:${record.id.toLowerCase()}`;
